import { useState, useRef } from "react";
import { useTranslation } from "react-i18next";

import { ToggleButton, ToggleButtonGroup } from "@mui/material";
import { styled } from "@mui/system";

import TextFieldComponent from "../../../TextField";
import UnitOptions from "../../../UnitOptions";

import { font, welcomeScreenUI, uxForceDesktopLayout } from "../../../../configs";
import { isChildren } from "../../../../utils/categoryParams";
import {
  capitalizeFirstLetter,
  getHeightCMPlaceholder,
  getHeightFeetGivenchyPlaceholder,
  getHeightFeetPlaceholder,
  getHeightInchPlaceholder,
  getWeightPlaceholder,
  isPhysicalAndroidDevice,
  useIsMobile,
} from "../../../../utils";
import { findBrandByDomain } from "../../../../configs/configLoader";
import { handleAnalytics } from "../../../../utils/tracking";

import "./index.css";

interface UserInfoBlockProps {
  error: any;
  height: string;
  unit: string;
  feet: string;
  inches: string;
  unitWeight: string;
  weight?: string | undefined;
  age: string;
  handleFieldChange: (e: any, type: string, blurSignal: string) => void;
  handleUnitChange: (newUnit: string) => void;
  handleUnitWeightChange: (newUnit: string) => void;
  setNeedValidate: React.Dispatch<React.SetStateAction<boolean>>;
  titlesStyles: React.CSSProperties;
  disableContinue: boolean;
  nextStep: () => void;
  ageChildrenYears: string;
  ageChildrenMonths: string;
  unitAge: string;
  handleUnitAgeChange: (newUnit: string) => void;
  company: string;
  setTouchedCompany: React.Dispatch<React.SetStateAction<boolean>>;
  setFeetTouched: React.Dispatch<React.SetStateAction<boolean>>;
  setInchesTouched: React.Dispatch<React.SetStateAction<boolean>>;
}

const UserInfoBlock: React.FC<UserInfoBlockProps> = ({
  error,
  height,
  unit,
  feet,
  inches,
  unitWeight,
  weight,
  age,
  handleFieldChange,
  handleUnitChange,
  handleUnitWeightChange,
  setNeedValidate,
  titlesStyles,
  disableContinue,
  nextStep,
  ageChildrenYears,
  ageChildrenMonths,
  unitAge,
  handleUnitAgeChange,
  company,
  setTouchedCompany,
  setFeetTouched,
  setInchesTouched,
}) => {
  const isMobileRender = useIsMobile();

  const { t } = useTranslation("components/intro");

  const urlParameters = new URLSearchParams(window.location.search);
  const countryCode = urlParameters.get("countryCode");

  const [valueAge, setValueAge] = useState<string>(age);
  const [blurSignal, setBlurSignal] = useState("");

  const fontFamily = `${font}, sans-serif`;

  const isAndroid = isPhysicalAndroidDevice();

  const brandDefined = findBrandByDomain();

  const isSRP = brandDefined?.name === "SRP";
  const isVB = brandDefined?.name === "Victoria Beckham";
  const isVelour = brandDefined?.name === "Velour Garments";
  const isSporty = brandDefined?.name === "Sporty & Rich";
  const isMarcyParis = brandDefined?.name === "Marcy Paris";
  const isGivenchy = brandDefined?.name === "Givenchy";
  const isTDF = brandDefined?.name === "TDF";
  const isDevernois = brandDefined?.name === "Devernois";
  const isGalore = brandDefined?.name === "Galore";
  const isBlackstore = brandDefined?.name === "Blackstore";
  const isSportyAndRich = brandDefined?.name === "Sporty & Rich";

  const isDevernoisEU = isDevernois && countryCode === "EU";

  const isTDFMobile = brandDefined?.name === "TDF" && isMobileRender;

  const galoreToggleStyle = isGalore
    ? { width: "32px", height: "17px", fontSize: "9px", padding: "9px 12px" }
    : {};
  const galoreWrapperStyle = isGalore ? { padding: "2px 6px" } : {};

  const isMobile = uxForceDesktopLayout ? false : isMobileRender;

  const inputRefs = [
    useRef<HTMLInputElement>(null),
    useRef<HTMLInputElement>(null),
    useRef<HTMLInputElement>(null),
    useRef<HTMLInputElement>(null),
    useRef<HTMLInputElement>(null),
    useRef<HTMLInputElement>(null),
  ];

  const formatUnit = (unit?: string) => {
    if (!unit) return t("Select");
    return unit.toUpperCase();
  };

  return (
    <>
      <span
        id="height-unit-announcement"
        aria-live="polite"
        className="sr-only"
      >
        {isGivenchy
          ? t("height.title", { value: formatUnit(unit) })
          : titlesStyles.textTransform === "capitalize"
            ? capitalizeFirstLetter(t("height.title"))
            : t("height.title")}
      </span>

      <span
        id="weight-unit-announcement"
        aria-live="polite"
        className="sr-only"
      >
        {isGivenchy
          ? t("weight.title", {
              value: unit ? unitWeight.toUpperCase() : t("Select"),
            })
          : titlesStyles.textTransform === "capitalize"
            ? capitalizeFirstLetter(t("weight.title"))
            : t("weight.title")}
      </span>

      <span id="age-unit-announcement" aria-live="polite" className="sr-only">
        {titlesStyles.textTransform === "capitalize"
          ? capitalizeFirstLetter(t("age.title"))
          : t("age.title")}
      </span>

      {isMobile && !isSRP ? (
        <WrapperBlock>
          <label
            htmlFor="height"
            style={{
              ...titlesStyles,
              color: error.height.error
                ? "#DA3939"
                : welcomeScreenUI.input_fields.title.fontColor,
              textTransform:
                titlesStyles.textTransform === "capitalize"
                  ? "none"
                  : (titlesStyles.textTransform as React.CSSProperties["textTransform"]),
              letterSpacing: (titlesStyles as any)?.letterSpacing,
              fontStyle: (welcomeScreenUI.input_fields.title as any)
                .fontStyle as React.CSSProperties["fontStyle"],
            }}
            dangerouslySetInnerHTML={{
              __html: isGivenchy
                ? t("height.title", {
                    value: formatUnit(unit),
                  })
                : titlesStyles.textTransform === "capitalize"
                  ? capitalizeFirstLetter(t("height.title"))
                  : t("height.title"),
            }}
          />
          <Wrapper style={galoreWrapperStyle}>
            <ToggleButtonGroup
              value={unit}
              exclusive
              onChange={(_: any, newUnit: any) => handleUnitChange(newUnit)}
            >
              <StyledToggleButton
                value="cm"
                aria-describedby="height-unit-announcement"
                aria-pressed={unit === "cm"}
                style={{
                  fontFamily,
                  ...galoreToggleStyle,
                }}
              >
                <span
                  style={{
                    position: "relative",
                    bottom: isAndroid && font === "Futura" ? "2px" : 0,
                  }}
                >
                  cm
                </span>
              </StyledToggleButton>
              {!isDevernoisEU ? (
                <StyledToggleButton
                  value="feet"
                  aria-describedby="height-unit-announcement"
                  aria-pressed={unit === "feet"}
                  style={{ fontFamily, ...galoreToggleStyle }}
                >
                  <span
                    style={{
                      position: "relative",
                      bottom: isAndroid && font === "Futura" ? "2px" : 0,
                    }}
                  >
                    feet
                  </span>
                </StyledToggleButton>
              ) : null}
            </ToggleButtonGroup>
          </Wrapper>
        </WrapperBlock>
      ) : (
        <label
          htmlFor="height"
          style={{
            ...titlesStyles,
            color: error.height.error
              ? "#DA3939"
              : welcomeScreenUI.input_fields.title.fontColor,
            textTransform:
              titlesStyles.textTransform === "capitalize"
                ? "none"
                : (titlesStyles.textTransform as React.CSSProperties["textTransform"]),
            fontStyle: (welcomeScreenUI.input_fields.title as any)
              .fontStyle as React.CSSProperties["fontStyle"],
          }}
          dangerouslySetInnerHTML={{
            __html: isGivenchy
              ? t("height.title", {
                  value: formatUnit(unit),
                })
              : titlesStyles.textTransform === "capitalize"
                ? capitalizeFirstLetter(t("height.title"))
                : t("height.title"),
          }}
        />
      )}
      <div className="input-container">
        {unit === "cm" ? (
          <>
            <div className="input-field-row">
              {(!isMobile || (isMobile && isSRP)) && (
                <UnitOptions
                  option1="cm"
                  option2="feet"
                  unit={unit}
                  handleUnitChange={handleUnitChange}
                />
              )}
              <TextFieldComponent
                id="height"
                value={height}
                unit={unit}
                unitWeight={unitWeight}
                placeholder={getHeightCMPlaceholder(t)}
                handleChange={(e: any) =>
                  handleFieldChange(e, "height", blurSignal)
                }
                handleClick={() => {
                  handleAnalytics("action", "height_click", null);
                }}
                setBlurSignal={setBlurSignal}
                setNeedValidate={setNeedValidate}
                error={error.height.error}
                errorType="height"
                index={0}
                inputRefs={inputRefs}
                disableContinue={disableContinue}
                nextStep={nextStep}
                unitAge={unitAge}
                setTouchedCompany={setTouchedCompany}
                setFeetTouched={setFeetTouched}
                setInchesTouched={setInchesTouched}
              />
            </div>
            <div
              style={{ height: "10px" }}
              className={`${isTDFMobile ? "baseErrorTitleMargin" : ""}`}
            >
              {isGivenchy && !error.height.error ? (
                <span
                  style={{
                    ...titlesStyles,
                    color: welcomeScreenUI.input_fields.title.fontColor,
                    textTransform: "lowercase",
                    display: "flex",
                    marginTop: "5px",
                    marginBottom: "20px",
                  }}
                >
                  {getHeightCMPlaceholder(t)}
                </span>
              ) : null}
              {error.height.error && (
                <span
                  className={`${
                    isTDF && !isMobileRender
                      ? "baseErrorTitleRelative"
                      : "baseErrorTitle"
                  }`}
                >
                  {error.height.message}
                </span>
              )}
            </div>
          </>
        ) : (
          <>
            <div className="input-field-row">
              {(!isMobile || (isMobile && isSRP)) && (
                <UnitOptions
                  option1="cm"
                  option2="feet"
                  unit={unit}
                  handleUnitChange={handleUnitChange}
                />
              )}
              <div
                style={{
                  display: "flex",
                  gap:
                    isVB || isVelour || isGivenchy
                      ? 0
                      : isMobile
                        ? "10px"
                        : "20px",
                  width:
                    isSRP && !isMobile
                      ? "calc(100% + 2px)"
                      : isBlackstore && !isMobile
                        ? "calc(100% - 5px)"
                        : isSportyAndRich && !isMobile
                          ? "calc(100% - 15px)"
                          : "100%",
                }}
              >
                <TextFieldComponent
                  id="feet"
                  value={feet === "" ? "" : `${feet}'`}
                  unit={unit}
                  unitWeight={unitWeight}
                  placeholder={getHeightFeetPlaceholder(t)}
                  handleChange={(event: any) => {
                    const newValue = event.target.value
                      ? event.target.value.replace(/\D/g, "")
                      : "";
                    handleFieldChange(newValue, "feet", blurSignal);

                    const cursorPosition = event.target.selectionStart;

                    setTimeout(() => {
                      event.target.setSelectionRange(
                        cursorPosition,
                        cursorPosition,
                      );
                    }, 0);
                  }}
                  setBlurSignal={setBlurSignal}
                  setNeedValidate={setNeedValidate}
                  error={error.height.error}
                  errorType="feet"
                  index={1}
                  inputRefs={inputRefs}
                  disableContinue={disableContinue}
                  nextStep={nextStep}
                  unitAge={unitAge}
                  setTouchedCompany={setTouchedCompany}
                  setFeetTouched={setFeetTouched}
                  setInchesTouched={setInchesTouched}
                />
                <TextFieldComponent
                  id="inches"
                  value={inches === "" ? "" : `${inches}"`}
                  unit={unit}
                  feetValue={feet}
                  unitWeight={unitWeight}
                  placeholder={getHeightInchPlaceholder(t)}
                  handleChange={(event: any) => {
                    const newValue = event.target.value
                      ? event.target.value.replace(/\D/g, "")
                      : "";
                    handleFieldChange(newValue, "inches", blurSignal);

                    const cursorPosition = event.target.selectionStart;

                    setTimeout(() => {
                      event.target.setSelectionRange(
                        cursorPosition,
                        cursorPosition,
                      );
                    }, 0);
                  }}
                  setBlurSignal={setBlurSignal}
                  setNeedValidate={setNeedValidate}
                  error={error.height.error}
                  errorType="inches"
                  index={2}
                  inputRefs={inputRefs}
                  disableContinue={disableContinue}
                  nextStep={nextStep}
                  unitAge={unitAge}
                  setTouchedCompany={setTouchedCompany}
                  setFeetTouched={setFeetTouched}
                  setInchesTouched={setInchesTouched}
                />
              </div>
            </div>
            <div
              style={{ height: "10px" }}
              className={`${isTDFMobile ? "baseErrorTitleMargin" : ""}`}
            >
              {isGivenchy && !error.height.error ? (
                <span
                  style={{
                    ...titlesStyles,
                    color: welcomeScreenUI.input_fields.title.fontColor,
                    textTransform: "lowercase",
                    display: "flex",
                    marginTop: "5px",
                    marginBottom: "20px",
                  }}
                >
                  {getHeightFeetGivenchyPlaceholder(t)}
                </span>
              ) : null}
              {error.height.error && (
                <span
                  className={`${
                    isTDF && !isMobileRender
                      ? "baseErrorTitleRelative"
                      : "baseErrorTitle"
                  }`}
                >
                  {error.height.message}
                </span>
              )}
            </div>
          </>
        )}
      </div>
      {isMobile && !isSRP ? (
        <WrapperBlock style={{ marginTop: "5px" }}>
          <label
            htmlFor="weight"
            style={{
              ...titlesStyles,
              color: error.weight.error
                ? "#DA3939"
                : welcomeScreenUI.input_fields.title.fontColor,
              textTransform:
                titlesStyles.textTransform === "capitalize"
                  ? "none"
                  : (titlesStyles.textTransform as React.CSSProperties["textTransform"]),
              fontStyle: (welcomeScreenUI.input_fields.title as any)
                .fontStyle as React.CSSProperties["fontStyle"],
              display: "flex",
              marginTop: isGivenchy ? "15px" : "0px",
            }}
            dangerouslySetInnerHTML={{
              __html: isGivenchy
                ? t("weight.title", {
                    value: unit ? unitWeight.toUpperCase() : t("Select"),
                  })
                : titlesStyles.textTransform === "capitalize"
                  ? capitalizeFirstLetter(t("weight.title"))
                  : t("weight.title"),
            }}
          />
          <Wrapper style={galoreWrapperStyle}>
            <ToggleButtonGroup
              value={unitWeight}
              exclusive
              onChange={(_: any, newUnitWeight: any) =>
                handleUnitWeightChange(newUnitWeight)
              }
            >
              <StyledToggleButton
                value="kg"
                aria-describedby="weight-unit-announcement"
                aria-pressed={unitWeight === "kg"}
                style={{ fontFamily, ...galoreToggleStyle }}
              >
                <span
                  style={{
                    position: "relative",
                    bottom: isAndroid && font === "Futura" ? "2px" : 0,
                  }}
                >
                  kg
                </span>
              </StyledToggleButton>
              {!isDevernoisEU ? (
                <StyledToggleButton
                  value="lbs"
                  aria-describedby="weight-unit-announcement"
                  aria-pressed={unitWeight === "lbs"}
                  style={{ fontFamily, ...galoreToggleStyle }}
                >
                  <span
                    style={{
                      position: "relative",
                      bottom: isAndroid && font === "Futura" ? "2px" : 0,
                    }}
                  >
                    lbs
                  </span>
                </StyledToggleButton>
              ) : null}
            </ToggleButtonGroup>
          </Wrapper>
        </WrapperBlock>
      ) : (
        <label
          htmlFor="weight"
          style={{
            ...titlesStyles,
            color: error.weight.error
              ? "#DA3939"
              : welcomeScreenUI.input_fields.title.fontColor,
            textTransform:
              titlesStyles.textTransform === "capitalize"
                ? "none"
                : (titlesStyles.textTransform as React.CSSProperties["textTransform"]),
            fontStyle: (welcomeScreenUI.input_fields.title as any)
              .fontStyle as React.CSSProperties["fontStyle"],
            display: "flex",
            marginTop: isGivenchy ? "20px" : "0px",
          }}
          dangerouslySetInnerHTML={{
            __html: isGivenchy
              ? t("weight.title", {
                  value: unit ? unitWeight.toUpperCase() : t("Select"),
                })
              : titlesStyles.textTransform === "capitalize"
                ? capitalizeFirstLetter(t("weight.title"))
                : t("weight.title"),
          }}
        />
      )}
      <div className="input-container">
        <div className="input-field-row">
          {(!isMobile || (isMobile && isSRP)) && (
            <UnitOptions
              option1="kg"
              option2="lbs"
              unit={unitWeight}
              handleUnitChange={handleUnitWeightChange}
            />
          )}
          <TextFieldComponent
            id="weight"
            value={weight}
            unit={unit}
            unitWeight={unitWeight}
            placeholder={getWeightPlaceholder(unitWeight, t)}
            handleChange={(e: any) => handleFieldChange(e, "weight", blurSignal)}
            handleClick={() => {
              handleAnalytics("action", "weight_click", null);
            }}
            setBlurSignal={setBlurSignal}
            setNeedValidate={setNeedValidate}
            error={error.weight.error}
            errorType="weight"
            index={3}
            inputRefs={inputRefs}
            disableContinue={disableContinue}
            nextStep={nextStep}
            unitAge={unitAge}
            setTouchedCompany={setTouchedCompany}
            setFeetTouched={setFeetTouched}
            setInchesTouched={setInchesTouched}
          />
        </div>
        <div
          style={{ height: "10px" }}
          className={`${isTDFMobile ? "baseErrorTitleMargin" : ""}`}
        >
          {isGivenchy && !error.weight.error ? (
            <span
              style={{
                ...titlesStyles,
                color: welcomeScreenUI.input_fields.title.fontColor,
                textTransform: "lowercase",
                display: "flex",
                marginTop: "5px",
                marginBottom: "20px",
              }}
            >
              {getWeightPlaceholder(unitWeight, t)}
            </span>
          ) : null}
          {error.weight.error && (
            <span
              className={`${
                isTDF && !isMobileRender
                  ? "baseErrorTitleRelative"
                  : "baseErrorTitle"
              }`}
            >
              {error.weight.message}
            </span>
          )}
        </div>
      </div>
      {isChildren ? (
        <>
          {isMobile && !isSRP ? (
            <WrapperBlock style={{ marginTop: "5px" }}>
              <label
                htmlFor="ageChildren"
                style={{
                  ...titlesStyles,
                  color: error.ageChildren.error
                    ? "#DA3939"
                    : welcomeScreenUI.input_fields.title.fontColor,
                  textTransform:
                    titlesStyles.textTransform === "capitalize"
                      ? "none"
                      : (titlesStyles.textTransform as React.CSSProperties["textTransform"]),
                  fontStyle: (welcomeScreenUI.input_fields.title as any)
                    .fontStyle as React.CSSProperties["fontStyle"],
                }}
              >
                {titlesStyles.textTransform === "capitalize"
                  ? capitalizeFirstLetter(t("age.title"))
                  : t("age.title")}
              </label>
              <Wrapper style={galoreWrapperStyle}>
                <ToggleButtonGroup
                  value={unitAge}
                  exclusive
                  onChange={(_: any, newUnitAge: any) =>
                    handleUnitAgeChange(newUnitAge)
                  }
                >
                  <StyledToggleButton
                    value="year"
                    aria-describedby="age-unit-announcement"
                    aria-pressed={unitAge === "year"}
                    style={{ fontFamily, ...galoreToggleStyle }}
                  >
                    <span
                      style={{
                        position: "relative",
                        bottom: isAndroid && font === "Futura" ? "2px" : 0,
                      }}
                    >
                      {t("age.children.year")}
                    </span>
                  </StyledToggleButton>
                  <StyledToggleButton
                    value="month"
                    aria-describedby="age-unit-announcement"
                    aria-pressed={unitAge === "month"}
                    style={{ fontFamily, ...galoreToggleStyle }}
                  >
                    <span
                      style={{
                        position: "relative",
                        bottom: isAndroid && font === "Futura" ? "2px" : 0,
                      }}
                    >
                      {t("age.children.month")}
                    </span>
                  </StyledToggleButton>
                </ToggleButtonGroup>
              </Wrapper>
            </WrapperBlock>
          ) : (
            <label
              htmlFor="ageChildren"
              style={{
                ...titlesStyles,
                color: error.ageChildren.error
                  ? "#DA3939"
                  : welcomeScreenUI.input_fields.title.fontColor,
                textTransform:
                  titlesStyles.textTransform === "capitalize"
                    ? "none"
                    : (titlesStyles.textTransform as React.CSSProperties["textTransform"]),
                fontStyle: (welcomeScreenUI.input_fields.title as any)
                  .fontStyle as React.CSSProperties["fontStyle"],
              }}
              dangerouslySetInnerHTML={{
                __html:
                  titlesStyles.textTransform === "capitalize"
                    ? capitalizeFirstLetter(t("age.title"))
                    : t("age.title"),
              }}
            />
          )}

          <div className="input-container">
            <div className="input-field-row">
              {(!isMobile || (isMobile && isSRP)) && (
                <UnitOptions
                  option1="year"
                  option2="month"
                  placeholder1={t("age.children.year")}
                  placeholder2={t("age.children.month")}
                  unit={unitAge}
                  handleUnitChange={handleUnitAgeChange}
                />
              )}

              {unitAge === "year" ? (
                <TextFieldComponent
                  id="ageChildrenYears"
                  value={ageChildrenYears}
                  unit={unit}
                  unitWeight={unitWeight}
                  placeholder={t("age.children.placeholder")}
                  handleChange={(e: any) =>
                    handleFieldChange(e, "year", blurSignal)
                  }
                  handleClick={() => {
                    handleAnalytics("action", "ageChildrenYears_click", null);
                  }}
                  setBlurSignal={setBlurSignal}
                  setNeedValidate={setNeedValidate}
                  error={error.ageChildren.error}
                  errorType="ageChildrenYears"
                  index={4}
                  inputRefs={inputRefs}
                  disableContinue={disableContinue}
                  nextStep={nextStep}
                  unitAge={unitAge}
                  setTouchedCompany={setTouchedCompany}
                  setFeetTouched={setFeetTouched}
                  setInchesTouched={setInchesTouched}
                />
              ) : (
                <div
                  style={{
                    display: "flex",
                    gap: isVB || isVelour ? "0px" : isMobile ? "10px" : "20px",
                    width: isMobileRender ? "100%" : "calc(100% + 2px)",
                  }}
                >
                  <TextFieldComponent
                    id="ageChildren"
                    value={ageChildrenMonths || ""}
                    unit={unit}
                    feetValue={feet}
                    unitWeight={unitWeight}
                    placeholder={t("age.placeholdermonths")}
                    handleChange={(e: any) => {
                      handleFieldChange(e, "month", blurSignal);

                      const cursorPosition = e.target.selectionStart;

                      setTimeout(() => {
                        e.target.setSelectionRange(
                          cursorPosition,
                          cursorPosition,
                        );
                      }, 0);
                    }}
                    setBlurSignal={setBlurSignal}
                    setNeedValidate={setNeedValidate}
                    error={error.ageChildren.error}
                    errorType="ageChildren"
                    index={6}
                    inputRefs={inputRefs}
                    disableContinue={disableContinue}
                    nextStep={nextStep}
                    unitAge={unitAge}
                    setTouchedCompany={setTouchedCompany}
                    setFeetTouched={setFeetTouched}
                    setInchesTouched={setInchesTouched}
                  />
                </div>
              )}
            </div>

            {unitAge === "year" ? (
              <div
                style={{ height: "10px" }}
                className={`${isTDFMobile ? "baseErrorTitleMargin" : ""}`}
              >
                {isGivenchy && !error.ageChildren.error ? (
                  <span
                    style={{
                      ...titlesStyles,
                      color: welcomeScreenUI.input_fields.title.fontColor,
                      textTransform: "lowercase",
                      display: "flex",
                      marginTop: "5px",
                      marginBottom: "20px",
                    }}
                  >
                    {t("age.children.placeholder")}
                  </span>
                ) : null}
                {error.ageChildren.error && (
                  <span
                    className={`${
                      isTDF && !isMobileRender
                        ? "baseErrorTitleRelative"
                        : "baseErrorTitle"
                    }`}
                  >
                    {error.ageChildren.message}
                  </span>
                )}
              </div>
            ) : (
              <div
                style={{ height: "10px" }}
                className={`${isTDFMobile ? "baseErrorTitleMargin" : ""}`}
              >
                {isGivenchy && !error.ageChildren.error ? (
                  <span
                    style={{
                      ...titlesStyles,
                      color: welcomeScreenUI.input_fields.title.fontColor,
                      textTransform: "lowercase",
                      display: "flex",
                      marginTop: "5px",
                      marginBottom: "20px",
                    }}
                  >
                    {t("age.placeholdermonths")}
                  </span>
                ) : null}
                {error.ageChildren.error && (
                  <span
                    className={`${
                      isTDF && !isMobileRender
                        ? "baseErrorTitleRelative"
                        : "baseErrorTitle"
                    }`}
                  >
                    {error.ageChildren.message}
                  </span>
                )}
              </div>
            )}
          </div>
        </>
      ) : (
        <>
          <label
            htmlFor="age"
            style={{
              ...titlesStyles,
              color: error.age.error
                ? "#DA3939"
                : welcomeScreenUI.input_fields.title.fontColor,
              textTransform:
                titlesStyles.textTransform === "capitalize"
                  ? "none"
                  : (titlesStyles.textTransform as React.CSSProperties["textTransform"]),
              fontStyle: (welcomeScreenUI.input_fields.title as any)
                .fontStyle as React.CSSProperties["fontStyle"],
              marginTop: isGivenchy ? "20px" : isMobile ? "10px" : "0px",
              marginBottom: isGivenchy
                ? "5px"
                : isTDF
                  ? "5px"
                  : isAndroid && isSporty
                    ? "10px"
                    : "0",
            }}
            dangerouslySetInnerHTML={{
              __html:
                titlesStyles.textTransform === "capitalize"
                  ? capitalizeFirstLetter(t("age.title"))
                  : t("age.title"),
            }}
          />
          <TextFieldComponent
            id="age"
            value={valueAge || ""}
            unit={age}
            unitWeight={unitWeight}
            setBlurSignal={setBlurSignal}
            setNeedValidate={setNeedValidate}
            placeholder={t("age.placeholder")}
            index={4}
            inputRefs={inputRefs}
            handleChange={(event: any) => {
              const newValue = event.target.value.replace(/\D/g, "");

              setValueAge(newValue);

              handleFieldChange(newValue, "age", blurSignal);

              const cursorPosition = event.target.selectionStart;

              setTimeout(() => {
                event.target.setSelectionRange(cursorPosition, cursorPosition);
              }, 0);
            }}
            handleClick={() => {
              handleAnalytics("action", "age_click", null);
            }}
            unitAge={unitAge}
            disableContinue={disableContinue}
            nextStep={nextStep}
            error={error.age.error}
            errorType="age"
            setTouchedCompany={setTouchedCompany}
            setFeetTouched={setFeetTouched}
            setInchesTouched={setInchesTouched}
          />
        </>
      )}

      <div
        style={{
          height: "2px",
          position: "relative",
          bottom: isGivenchy ? 0 : isTDF ? "5px" : "10px",
        }}
      >
        {isGivenchy && !error.age.error ? (
          <span
            style={{
              ...titlesStyles,
              color: welcomeScreenUI.input_fields.title.fontColor,
              textTransform: "lowercase",
              display: "flex",
              marginBottom: "20px",
            }}
          >
            {t("age.placeholder")}
          </span>
        ) : null}
        {error.age.error && (
          <span className="ageErrorTitle">{error.age.message}</span>
        )}
      </div>

      {isMarcyParis ? (
        <>
          <label
            htmlFor="company"
            style={{
              ...titlesStyles,
              color: error.age.error
                ? "#DA3939"
                : welcomeScreenUI.input_fields.title.fontColor,
              textTransform:
                titlesStyles.textTransform === "capitalize"
                  ? "none"
                  : (titlesStyles.textTransform as React.CSSProperties["textTransform"]),
              fontStyle: (welcomeScreenUI.input_fields.title as any)
                .fontStyle as React.CSSProperties["fontStyle"],
              marginTop: isMobile ? "10px" : "0px",
              marginBottom: isAndroid && isSporty ? "10px" : "0",
            }}
            dangerouslySetInnerHTML={{
              __html:
                titlesStyles.textTransform === "capitalize"
                  ? capitalizeFirstLetter(t("company.title"))
                  : t("company.title"),
            }}
          />
          <TextFieldComponent
            id="company"
            value={company}
            unit={unit}
            unitWeight={unitWeight}
            placeholder={t("company.placeholder")}
            handleChange={(e: any) =>
              handleFieldChange(e, "company", blurSignal)
            }
            handleClick={() => {
              handleAnalytics("action", "company_click", null);
            }}
            setBlurSignal={setBlurSignal}
            setNeedValidate={setNeedValidate}
            error={error.company.error}
            errorType="company"
            index={5}
            inputRefs={inputRefs}
            disableContinue={disableContinue}
            nextStep={nextStep}
            unitAge={unitAge}
            setTouchedCompany={setTouchedCompany}
            setFeetTouched={setFeetTouched}
            setInchesTouched={setInchesTouched}
          />
          <div
            style={{ height: "10px" }}
            className={`${isTDFMobile ? "baseErrorTitleMargin" : ""}`}
          >
            {error.company.error && (
              <span
                className={`${
                  isTDF && !isMobileRender
                    ? "baseErrorTitleRelative"
                    : "baseErrorTitle"
                }`}
              >
                {error.company.message}
              </span>
            )}
          </div>
        </>
      ) : null}
    </>
  );
};

const WrapperBlock = styled("div")({
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
});

const Wrapper = styled("div")({
  display: "inline-block",
  backgroundColor: "#F7F7F7",
  borderRadius: "27px",
  padding: "1px 5px",
});

const StyledToggleButton = styled(ToggleButton)({
  width: "28px",
  height: "15px",
  fontSize: "8px",
  fontWeight: 500,
  textTransform: "none",
  padding: "8px 10px",
  position: "relative",
  bottom: "1px",
  "&.Mui-selected": {
    backgroundColor: welcomeScreenUI.units.activeColor,
    color: "#FFFFFF",
    borderColor: "transparent",
    borderRadius: "17px",
    margin: "2px 0",
  },
  "&.Mui-selected: hover": {
    backgroundColor: welcomeScreenUI.units.activeColor,
    color: "#FFFFFF",
    borderColor: "transparent",
    borderRadius: "17px",
    margin: "2px 0",
  },
  "&:not(.Mui-selected)": {
    color: welcomeScreenUI.units.activeColor,
    backgroundColor: "transparent",
    borderColor: "transparent",
    borderRadius: "17px",
    margin: "2px 0",
  },
});

export default UserInfoBlock;
